import { useTranslation } from "react-i18next";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  addTalentsToPool,
  fetchWorkLocationTalentPool,
  removeTalentsFromPool,
  selectAllCustomerWorkLocations,
} from "../customerWorkLocationsSlice";
import { PoolTalent, WorkLocationTalentPool } from "../customerAPI";
import { TalentPoolAddDialog } from "./TalentPoolAddDialog";
import { isFulfilled } from "@reduxjs/toolkit";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { ActionButtonsContainer, ModalContainer } from "../styles";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../app/constants";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { canExpertEditCustomer } from "../../user/accessRightChecks";
import isEmpty from "../../../common/helpers/isEmpty";
import { Box, Typography } from "@mui/material";
import { TalentStatus } from "../../talents/types/common";
import TalentStatusIcon from "../../../common/components/functional/talentStatus/TalentStatusIcon";
import { getName, sortByName } from "../../../common/helpers/common";

type Props = {
  workLocationId: string | undefined;
  onClose: () => void;
};

export const TalentPoolInfo = ({ workLocationId, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const headers = [
    {
      text: t("talentProfile:state"),
      tooltip: t("customers:poolsNS:talentStatusPoolInfoTooltip"),
    },
    { text: t("common:name") },
  ];
  const addButtonLabel = t("customers:poolsNS:addTalent");
  const columnWidths = ["10%", "auto"];

  const workLocation = useAppSelector((state) =>
    selectAllCustomerWorkLocations(state),
  ).find((workLocation) => workLocation.id === workLocationId);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const [poolTalents, setPoolTalents] = useState<PoolTalent[]>([]);
  const tableData = poolTalents.map((talent) => ({
    values: {
      status: <TalentStatusIcon status={talent.status} />,
      name: getName(talent),
    },
    id: talent.id,
    deletable: true,
    linkTo: `/home/talents/${talent.id}/profile`,
  }));

  const [saveNewTalentIds, setSaveNewTalentIds] = useState<string[]>([]);
  const [removeTalentIds, setRemoveTalentIds] = useState<string[]>([]);

  const disableSave = isEmpty(saveNewTalentIds) && isEmpty(removeTalentIds);

  const deleteTalent = (id: string) => {
    setRemoveTalentIds((prev) => [...prev, id]);
    setPoolTalents((prev) => prev.filter((talent) => talent.id !== id));
  };

  const addToTalents = (talents: AutocompleteType[]) => {
    setSaveNewTalentIds((prev) => [
      ...prev,
      ...talents.map((talent) => talent.id),
    ]);
    setPoolTalents((prev) => [
      ...prev,
      ...talents.map((talent) => ({
        id: talent.id,
        lastName: talent.name.split(" ")[0],
        firstName: talent.name.split(" ")[1],
        status: TalentStatus.ACTIVE,
      })),
    ]);
  };

  const saveChanges = () => {
    if (!workLocationId) return;

    // Cross reference talents to be removed and added and filter out duplicates
    const toBeSaved = saveNewTalentIds.filter(
      (id) => !removeTalentIds.includes(id),
    );
    const toBeRemoved = removeTalentIds.filter(
      (id) => !saveNewTalentIds.includes(id),
    );

    if (!isEmpty(toBeSaved)) {
      dispatch(addTalentsToPool({ workLocationId, talentIds: toBeSaved })).then(
        (action) => {
          if (isFulfilled(action)) {
            setSaveNewTalentIds([]);
          }
        },
      );
    }

    if (!isEmpty(toBeRemoved)) {
      dispatch(
        removeTalentsFromPool({ workLocationId, talentIds: toBeRemoved }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setRemoveTalentIds([]);
        }
      });
    }

    onClose();
  };

  useEffect(() => {
    if (!workLocationId) return;
    dispatch(
      fetchWorkLocationTalentPool({
        workLocationId,
        includeNotActiveTalents: true,
      }),
    ).then((result) => {
      const payload = result.payload as WorkLocationTalentPool;
      setPoolTalents(sortByName(payload?.talents));
    });
  }, [dispatch, workLocationId]);

  return (
    <ModalContainer sx={{ cursor: "auto" }} className={NO_DRAG_SELECTOR}>
      <Box display="flex" alignItems="center" gap="1rem" marginTop="1rem">
        <Typography variant="header" fontSize="22px" fontWeight="600">
          {t("customers:poolsNS:pool")}
        </Typography>
        <Typography>
          {workLocation?.name} {`(${workLocation?.customerOrgName})`}
        </Typography>
      </Box>

      <FormTable
        data={tableData}
        headers={headers}
        addButtonLabel={addButtonLabel}
        columnWidths={columnWidths}
        handleOpenEditModal={() => setIsAddDialogOpen(true)}
        handleDelete={deleteTalent}
        disableEditOnly
        addButtonVariant="text"
      />
      <TalentPoolAddDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        workLocationId={workLocationId}
        updateTalents={addToTalents}
      />
      <ActionButtonsContainer
        className={DEFAULT_DRAG_HANDLE_SELECTOR}
        marginTop="1rem"
      >
        <CustomButton
          accessRightChecks={[canExpertEditCustomer]}
          disabled={disableSave}
          variant="contained"
          sx={{ borderRadius: "18px", height: "36px" }}
          onClick={saveChanges}
        >
          {t("common:save")}
        </CustomButton>
        <CustomButton
          variant="outlined"
          sx={{
            borderRadius: "18px",
            height: "36px",
          }}
          onClick={onClose}
        >
          {t("common:cancel")}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};
